/**
 * GradientEditor — Main 3-level gradient editor component
 *
 * Replaces the 2-color-picker Gradient accordion group.
 * Levels: Simple (2 colors) → Medium (+angle, +shimmer) → Advanced (multi-stop, full controls)
 *
 * Level is derived from current data (not stored).
 * DB always stores full GradientOverlay.
 *
 * TestId convention: `style-group-gradient-{control}`
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import type { GradientOverlay } from '@segmently/schema-models/v2';
import { ColorInput } from './ColorInput';
import { GradientStopEditor } from './GradientStopEditor';
import { ShimmerControls } from './ShimmerControls';
import { StyledNumberInput } from '@modules/ui-components';
import {
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  fieldLabelSx,
  STYLE_FIELD_LABEL_HEIGHT,
} from '../../../config/editorFieldStyles';
import { FIELD_GAP_SMALL } from '../../../config/ui';
import {
  type GradientLevel,
  detectGradientLevel,
  buildSimpleGradient,
  buildMediumGradient,
  extractColors,
  extractStops,
  stopsToColorsLocations,
  gradientToCSS,
  ANGLE_PRESETS,
  DEFAULT_GRADIENT,
  buildGradientFromBaseColor,
} from '../../../utils/gradientUtils';

export interface GradientEditorProps {
  /** Current gradient value (full GradientOverlay from DB) */
  value: GradientOverlay | null | undefined;
  /** Callback to write full gradient object */
  onChange: (gradient: GradientOverlay | null) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Base color for deriving gradient when toggled ON (e.g., button backgroundColor) */
  baseColor?: string;
}

export const GradientEditor: React.FC<GradientEditorProps> = ({
  value,
  onChange,
  disabled = false,
  baseColor,
}) => {
  const isEnabled = value != null;
  const detectedLevel = useMemo(() => detectGradientLevel(value), [value]);
  // Allow user to override level upward (e.g., switch from simple to advanced)
  const [levelOverride, setLevelOverride] = useState<GradientLevel | null>(null);
  const level = levelOverride ?? detectedLevel;

  const { startColor, endColor } = useMemo(() => extractColors(value), [value]);
  const stops = useMemo(() => extractStops(value), [value]);
  const angle = value?.angle ?? 90;
  const previewCSS = useMemo(() => gradientToCSS(value), [value]);

  // --- Enable toggle ---
  const handleToggle = useCallback(
    (_: unknown, checked: boolean) => {
      if (checked) {
        // Derive gradient from button's background color if available
        onChange(baseColor ? buildGradientFromBaseColor(baseColor) : { ...DEFAULT_GRADIENT });
      } else {
        onChange(null);
      }
      setLevelOverride(null);
    },
    [onChange, baseColor],
  );

  // --- Level switch ---
  const handleLevelChange = useCallback(
    (_: unknown, newLevel: GradientLevel | null) => {
      if (!newLevel || !value) return;
      setLevelOverride(newLevel);

      // Preserve colors when switching levels
      if (newLevel === 'simple') {
        onChange(buildSimpleGradient(startColor, endColor));
      } else if (newLevel === 'medium') {
        onChange(buildMediumGradient(startColor, endColor, angle, false));
      }
      // 'advanced' keeps current data as-is
    },
    [value, onChange, startColor, endColor, angle],
  );

  // --- Simple/Medium: color changes ---
  const handleStartColorChange = useCallback(
    (color: unknown) => {
      if (!value) return;
      const c = color as string;
      if (level === 'simple') {
        onChange(buildSimpleGradient(c, endColor));
      } else if (level === 'medium') {
        onChange(buildMediumGradient(c, endColor, angle, value.shimmer?.enabled === true));
      }
    },
    [value, level, onChange, endColor, angle],
  );

  const handleEndColorChange = useCallback(
    (color: unknown) => {
      if (!value) return;
      const c = color as string;
      if (level === 'simple') {
        onChange(buildSimpleGradient(startColor, c));
      } else if (level === 'medium') {
        onChange(buildMediumGradient(startColor, c, angle, value.shimmer?.enabled === true));
      }
    },
    [value, level, onChange, startColor, angle],
  );

  // --- Medium: angle change ---
  const handleAngleChange = useCallback(
    (e: { target: { value: unknown } }) => {
      if (!value) return;
      const newAngle = Number(e.target.value);
      onChange(
        buildMediumGradient(startColor, endColor, newAngle, value.shimmer?.enabled === true),
      );
    },
    [value, onChange, startColor, endColor],
  );

  // --- Medium: shimmer toggle ---
  const handleMediumShimmerChange = useCallback(
    (shimmer: unknown) => {
      if (!value) return;
      onChange({
        ...value,
        shimmer: shimmer as GradientOverlay['shimmer'],
      });
    },
    [value, onChange],
  );

  // --- Advanced: stops change ---
  const handleStopsChange = useCallback(
    (newStops: GradientOverlay['stops']) => {
      if (!value || !newStops) return;
      const { colors, locations } = stopsToColorsLocations(newStops);
      onChange({ ...value, stops: newStops, colors, locations });
    },
    [value, onChange],
  );

  // --- Advanced: angle input ---
  const handleAdvancedAngleChange = useCallback(
    (newAngle: number | null) => {
      if (!value) return;
      onChange({ ...value, angle: newAngle ?? 90 });
    },
    [value, onChange],
  );

  // --- Advanced: size input ---
  const handleSizeChange = useCallback(
    (newSize: number | null) => {
      if (!value) return;
      onChange({ ...value, size: newSize ?? 100 });
    },
    [value, onChange],
  );

  // --- Advanced: shimmer full config ---
  const handleAdvancedShimmerChange = useCallback(
    (shimmer: GradientOverlay['shimmer']) => {
      if (!value) return;
      onChange({ ...value, shimmer: shimmer ?? undefined });
    },
    [value, onChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="style-group-gradient">
      {/* Enable toggle — styled to match StyleFieldRow checkboxes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL, height: STYLE_FIELD_LABEL_HEIGHT, minHeight: STYLE_FIELD_LABEL_HEIGHT }}>
        <Checkbox
          size="small"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={disabled}
          icon={<Box sx={checkboxIconSx} />}
          checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          sx={checkboxSx}
          data-testid="style-group-gradient-enable"
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={fieldLabelSx}
        >
          Enable gradient
        </Typography>
      </Box>

      {isEnabled && (
        <>
          {/* Level selector */}
          <ToggleButtonGroup
            value={level}
            exclusive
            onChange={handleLevelChange}
            size="small"
            disabled={disabled}
            sx={{ alignSelf: 'stretch' }}
            data-testid="style-group-gradient-level"
          >
            <ToggleButton value="simple" sx={{ flex: 1, fontSize: 11, py: 0.25, textTransform: 'none' }}>
              Simple
            </ToggleButton>
            <ToggleButton value="medium" sx={{ flex: 1, fontSize: 11, py: 0.25, textTransform: 'none' }}>
              Medium
            </ToggleButton>
            <ToggleButton value="advanced" sx={{ flex: 1, fontSize: 11, py: 0.25, textTransform: 'none' }}>
              Advanced
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Preview bar */}
          <Box
            sx={{
              height: 24,
              borderRadius: 1,
              background: previewCSS,
              border: '1px solid',
              borderColor: 'divider',
            }}
            data-testid="style-group-gradient-preview"
          />

          {/* === Simple Mode === */}
          {level === 'simple' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                  Start
                </Typography>
                <ColorInput
                  value={startColor}
                  onChange={handleStartColorChange}
                  disabled={disabled}
                  testId="style-group-gradient-start-color"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                  End
                </Typography>
                <ColorInput
                  value={endColor}
                  onChange={handleEndColorChange}
                  disabled={disabled}
                  testId="style-group-gradient-end-color"
                />
              </Box>
            </Box>
          )}

          {/* === Medium Mode === */}
          {level === 'medium' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                    Start
                  </Typography>
                  <ColorInput
                    value={startColor}
                    onChange={handleStartColorChange}
                    disabled={disabled}
                    testId="style-group-gradient-start-color"
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                    End
                  </Typography>
                  <ColorInput
                    value={endColor}
                    onChange={handleEndColorChange}
                    disabled={disabled}
                    testId="style-group-gradient-end-color"
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ minWidth: 40 }}>
                  Angle
                </Typography>
                <Select
                  size="small"
                  value={angle}
                  onChange={handleAngleChange}
                  disabled={disabled}
                  data-testid="style-group-gradient-angle"
                  sx={{ flex: 1, fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
                >
                  {ANGLE_PRESETS.map((p) => (
                    <MenuItem key={p.value} value={p.value} sx={{ fontSize: 12 }}>
                      {p.value}&deg; {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <ShimmerControls
                shimmer={value?.shimmer ?? null}
                onChange={handleMediumShimmerChange}
                mode="medium"
                disabled={disabled}
              />
            </Box>
          )}

          {/* === Advanced Mode === */}
          {level === 'advanced' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>
                  Color Stops
                </Typography>
                <GradientStopEditor
                  stops={stops}
                  onChange={handleStopsChange}
                  disabled={disabled}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                    Angle
                  </Typography>
                  <StyledNumberInput
                    value={angle}
                    onChange={handleAdvancedAngleChange}
                    min={0}
                    max={360}
                    disabled={disabled}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                    Size %
                  </Typography>
                  <StyledNumberInput
                    value={value?.size ?? 100}
                    onChange={handleSizeChange}
                    min={50}
                    max={200}
                    disabled={disabled}
                  />
                </Box>
              </Box>
              <ShimmerControls
                shimmer={value?.shimmer ?? null}
                onChange={handleAdvancedShimmerChange}
                mode="advanced"
                disabled={disabled}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
